import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitService from '../../services/unitService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './ProductCategoryEditPage.css';
import { ArrowLeft, Save } from 'lucide-react';

// Max length constants for validation
const MAX_LENGTHS = {
  unitName: 30,
  unitShortName: 10,
  description: 200,
};

interface FieldErrors {
  unitName?: string;
  unitShortName?: string;
  description?: string;
}

interface UnitForm {
  unitName: string;
  unitShortName: string;
  description: string;
  unitCategory: string;
  decimalAllowed: boolean;
  isBaseUnit: boolean;
  isActive: boolean;
}

const UnitEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [unit, setUnit] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<UnitForm>({
    unitName: '',
    unitShortName: '',
    description: '',
    unitCategory: '',
    decimalAllowed: false,
    isBaseUnit: true,
    isActive: true,
  });

  useEffect(() => {
    loadUnit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUnit = async () => {
    setLoading(true);
    setError(null);
    try {
      const unitData = await unitService.getUnitById(parseInt(id || '0'));
      if (unitData) {
        setUnit(unitData);
        setFormData({
          unitName: unitData.unitName || '',
          unitShortName: unitData.unitShortName || '',
          description: unitData.description || '',
          unitCategory: unitData.unitCategory || '',
          decimalAllowed: unitData.decimalAllowed || false,
          isBaseUnit: unitData.isBaseUnit || false,
          isActive: unitData.isActive !== false,
        });
      }
      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('unit.failedToLoadUnit')).message);
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let finalValue: any;
    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (!isCheckbox) {
      // Clear field error when user starts typing
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Validate Unit Name
    if (!formData.unitName.trim()) {
      errors.unitName = t('validation.unitNameRequired');
      isValid = false;
    } else if (formData.unitName.length > MAX_LENGTHS.unitName) {
      errors.unitName = t('unit.formValidation.nameMaxLength');
      isValid = false;
    }

    // Validate Abbreviation
    if (!formData.unitShortName.trim()) {
      errors.unitShortName = t('validation.unitShortNameRequired');
      isValid = false;
    } else if (formData.unitShortName.length > MAX_LENGTHS.unitShortName) {
      errors.unitShortName = t('unit.formValidation.shortNameMaxLength');
      isValid = false;
    }

    // Validate Description (optional — max length only)
    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('unit.formValidation.descriptionMaxLength');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || !unit) {
      return;
    }

    setSaving(true);
    try {
      await unitService.updateUnit(unit.unitId, formData);
      navigate('/products/units', {
        state: { message: t('unit.updateSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('unit.failedToUpdateUnit')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!unit) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{t('unit.failedToLoadUnit')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/units')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('unit.editUnit')}
              <span className="ims-page-subtitle">{unit.unitName}</span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/units')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          {/* Form Section */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('unit.editUnit')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  {/* Unit Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unit.unitName')} <span className="required">*</span>
                      <span className="char-counter">{formData.unitName.length}/{MAX_LENGTHS.unitName}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.unitName ? 'is-invalid' : ''}`}
                      name="unitName"
                      value={formData.unitName}
                      onChange={handleInputChange}
                      placeholder={t('unit.unitName')}
                      maxLength={MAX_LENGTHS.unitName}
                    />
                    {fieldErrors.unitName && <small className="form-error">{fieldErrors.unitName}</small>}
                    {!fieldErrors.unitName && <small className="form-helper">Update the unit name if needed</small>}
                  </div>

                  {/* Abbreviation */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unit.abbreviation')} <span className="required">*</span>
                      <span className="char-counter">{formData.unitShortName.length}/{MAX_LENGTHS.unitShortName}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.unitShortName ? 'is-invalid' : ''}`}
                      name="unitShortName"
                      value={formData.unitShortName}
                      onChange={handleInputChange}
                      placeholder="e.g., kg, m, L"
                      maxLength={MAX_LENGTHS.unitShortName}
                    />
                    {fieldErrors.unitShortName && <small className="form-error">{fieldErrors.unitShortName}</small>}
                    {!fieldErrors.unitShortName && <small className="form-helper">Short form of unit (max 10 characters)</small>}
                  </div>

                  {/* Unit Category */}
                  <div className="form-group">
                    <label className="form-label">{t('unit.unitCategory')}</label>
                    <select
                      className="form-input"
                      name="unitCategory"
                      value={formData.unitCategory}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('unit.selectCategory')}</option>
                      <option value="Weight">{t('unit.categories.weight')}</option>
                      <option value="Volume">{t('unit.categories.volume')}</option>
                      <option value="Length">{t('unit.categories.length')}</option>
                      <option value="Area">{t('unit.categories.area')}</option>
                      <option value="Quantity">{t('unit.categories.quantity')}</option>
                      <option value="Time">{t('unit.categories.time')}</option>
                      <option value="Temperature">{t('unit.categories.temperature')}</option>
                      <option value="Other">{t('unit.categories.other')}</option>
                    </select>
                    <small className="form-helper">Category of unit (optional)</small>
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unit.description')}
                      <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t('unit.description')}
                      rows={4}
                      maxLength={MAX_LENGTHS.description}
                    />
                    {fieldErrors.description && <small className="form-error">{fieldErrors.description}</small>}
                    {!fieldErrors.description && <small className="form-helper">Optional: describe the unit of measure</small>}
                  </div>

                  {/* Decimal Allowed */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="decimalAllowed"
                        checked={formData.decimalAllowed}
                        onChange={handleInputChange}
                      />
                      <span>{t('unit.decimalAllowed')}</span>
                    </label>
                  </div>

                  {/* Is Base Unit */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="isBaseUnit"
                        checked={formData.isBaseUnit}
                        onChange={handleInputChange}
                      />
                      <span>{t('unit.isBaseUnit')}</span>
                    </label>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="info-item">
                  <label className="info-label">{t('unit.unitCode')}</label>
                  <div className="info-value">{unit.unitCode}</div>
                  <small style={{ fontSize: '11px', color: '#9ca3af' }}>Auto-generated — cannot be modified</small>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('common.status')}</label>
                  <div className="info-value">
                    <span className="info-badge info-badge-active">
                      {unit.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('common.createdDate')}</label>
                  <div className="info-value">
                    {unit.createdDate ? new Date(unit.createdDate).toLocaleDateString('en-US') : '-'}
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('common.createdTime')}</label>
                  <div className="info-value">
                    {unit.createdDate ? new Date(unit.createdDate).toLocaleTimeString('en-US') : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitEditPage;
